/**
 * Show from / show until on a page's blocks (ADR 0031, amendment of 2026-10-03). The database stores two instants and
 * decides at each visitor's request; staff pick dates and times as the church's clock reads them, whatever zone their
 * own computer is in. Everything here is pure, so the zone arithmetic is tested without a browser.
 */

/** A block's window as the editor holds it and save_page_blocks takes it: ISO instants, either end optional. */
export interface BlockScheduleValue {
  readonly showFrom?: string;
  readonly showUntil?: string;
}

/** A wall-clock date and time in the church's zone, as the date and time inputs give them. */
export interface WallClock {
  /** YYYY-MM-DD, or empty for no date. */
  readonly date: string;
  /** HH:MM, or empty for the whole day. */
  readonly time: string;
}

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** The zone's offset from UTC at an instant, in milliseconds (positive east of Greenwich). */
function offsetAt(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  return asUtc - Math.floor(instant / 1000) * 1000;
}

/**
 * The instant a wall-clock date and time in the zone names. Corrected twice, so a date either side of a
 * daylight-saving change takes that date's own offset. A time inside the hour skipped in spring names no instant; it
 * resolves to one beside the gap rather than failing.
 */
export function zonedToInstant(date: string, time: string, timeZone: string): Date | null {
  const d = DATE.exec(date);
  if (d === null) return null;
  const t = time === "" ? ["", "00", "00"] : TIME.exec(time);
  if (t === null) return null;
  const naive = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]));
  if (Number.isNaN(naive)) return null;
  let instant = naive - offsetAt(naive, timeZone);
  instant = naive - offsetAt(instant, timeZone);
  return new Date(instant);
}

/** The wall-clock date and time an instant reads as in the zone. */
export function instantToZoned(iso: string, timeZone: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
  };
}

/** The calendar day before a YYYY-MM-DD date, and after. Plain calendar arithmetic, no zone involved. */
function shiftDate(date: string, days: number): string {
  const d = DATE.exec(date);
  if (d === null) return date;
  const shifted = new Date(Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]) + days));
  return shifted.toISOString().slice(0, 10);
}

/*
 * The form's two halves. "Show from" with no time starts at the beginning of that day. "Show until" with no time
 * includes the whole of that day, so it is stored as midnight at the start of the next day (the window's end is
 * exclusive), and read back the same way. "Until Apr 6" therefore means what a church means by it: Easter Monday
 * still shows the banner, Tuesday does not.
 */

export function fromToInstant(value: WallClock, timeZone: string): string | undefined {
  if (value.date === "") return undefined;
  return zonedToInstant(value.date, value.time, timeZone)?.toISOString();
}

export function untilToInstant(value: WallClock, timeZone: string): string | undefined {
  if (value.date === "") return undefined;
  const instant =
    value.time === ""
      ? zonedToInstant(shiftDate(value.date, 1), "", timeZone)
      : zonedToInstant(value.date, value.time, timeZone);
  return instant?.toISOString();
}

export function instantToFrom(iso: string | undefined, timeZone: string): WallClock {
  if (iso === undefined) return { date: "", time: "" };
  const zoned = instantToZoned(iso, timeZone);
  return { date: zoned.date, time: zoned.time === "00:00" ? "" : zoned.time };
}

export function instantToUntil(iso: string | undefined, timeZone: string): WallClock {
  if (iso === undefined) return { date: "", time: "" };
  const zoned = instantToZoned(iso, timeZone);
  return zoned.time === "00:00"
    ? { date: shiftDate(zoned.date, -1), time: "" }
    : { date: zoned.date, time: zoned.time };
}

/** Whether a schedule says anything: a block without one shows always. */
export function hasSchedule(schedule: BlockScheduleValue | null | undefined): boolean {
  return schedule?.showFrom !== undefined || schedule?.showUntil !== undefined;
}

export type ScheduleState = "always" | "upcoming" | "showing" | "ended";

/** Where the block's window stands at an instant: from is inclusive, until exclusive, as in the database. */
export function scheduleState(
  schedule: BlockScheduleValue | null | undefined,
  now: Date,
): ScheduleState {
  if (!hasSchedule(schedule)) return "always";
  const at = now.getTime();
  if (schedule?.showFrom !== undefined && at < Date.parse(schedule.showFrom)) return "upcoming";
  if (schedule?.showUntil !== undefined && at >= Date.parse(schedule.showUntil)) return "ended";
  return "showing";
}

/** "Mar 30", with the year when it is not this year's, and the time when it is not the whole day. */
function describe(clock: WallClock, now: WallClock, locale: string): string {
  const d = DATE.exec(clock.date);
  if (d === null) return "";
  const day = new Date(Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), 12));
  const sameYear = clock.date.slice(0, 4) === now.date.slice(0, 4);
  const date = day.toLocaleDateString(locale, {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  if (clock.time === "") return date;
  const t = TIME.exec(clock.time);
  const time = new Date(Date.UTC(2000, 0, 1, Number(t?.[1] ?? 0), Number(t?.[2] ?? 0)));
  return `${date}, ${time.toLocaleTimeString(locale, { timeZone: "UTC", hour: "numeric", minute: "2-digit" })}`;
}

export interface ScheduleChips {
  /** The window in words: "Shows Mar 30 – Apr 6", "Shows from Mar 30", "Shows until Apr 6". */
  readonly window: string;
  /** Where it stands now, when visitors do not see it: "Scheduled" before it starts, "Hidden now" after it ends. */
  readonly status: "Scheduled" | "Hidden now" | null;
}

/** What the block card says about a block's window, read on the church's clock. Null for a block with none. */
export function scheduleChips(
  schedule: BlockScheduleValue | null | undefined,
  now: Date,
  timeZone: string,
  locale = "en-US",
): ScheduleChips | null {
  if (!hasSchedule(schedule)) return null;
  const today = instantToZoned(now.toISOString(), timeZone);
  const from = describe(instantToFrom(schedule?.showFrom, timeZone), today, locale);
  const until = describe(instantToUntil(schedule?.showUntil, timeZone), today, locale);
  const window =
    from !== "" && until !== ""
      ? `Shows ${from} – ${until}`
      : from !== ""
        ? `Shows from ${from}`
        : `Shows until ${until}`;
  const state = scheduleState(schedule, now);
  return {
    window,
    status: state === "upcoming" ? "Scheduled" : state === "ended" ? "Hidden now" : null,
  };
}

/** What is wrong with a window the form describes, in words, or null. */
export function scheduleProblem(
  from: WallClock,
  until: WallClock,
  timeZone: string,
): string | null {
  if (from.date === "" && from.time !== "") return "Choose a date to go with the show from time.";
  if (until.date === "" && until.time !== "")
    return "Choose a date to go with the show until time.";
  const start = fromToInstant(from, timeZone);
  const end = untilToInstant(until, timeZone);
  if (from.date !== "" && start === undefined) return "The show from date or time is not valid.";
  if (until.date !== "" && end === undefined) return "The show until date or time is not valid.";
  if (start !== undefined && end !== undefined && Date.parse(end) <= Date.parse(start)) {
    return "Show until must be after show from.";
  }
  return null;
}

/** The church's zone when it has a valid one, else the reader's own. */
export function usableTimeZone(zone: string | null | undefined): string {
  if (zone !== null && zone !== undefined && zone.length > 0) {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: zone });
      return zone;
    } catch {
      // An unknown zone falls through to the reader's own.
    }
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** The schedule section of the block drawer: Always, or between two optional wall-clock moments. */
export interface ScheduleForm {
  readonly mode: "always" | "dates";
  readonly from: WallClock;
  readonly until: WallClock;
}

export function scheduleToForm(
  schedule: BlockScheduleValue | null | undefined,
  timeZone: string,
): ScheduleForm {
  return {
    mode: hasSchedule(schedule) ? "dates" : "always",
    from: instantToFrom(schedule?.showFrom, timeZone),
    until: instantToUntil(schedule?.showUntil, timeZone),
  };
}

/**
 * The schedule the form describes, or null for always (including "between dates" with neither date chosen). Call
 * `scheduleProblem` first: an invalid half is left out here rather than reported.
 */
export function formToSchedule(form: ScheduleForm, timeZone: string): BlockScheduleValue | null {
  if (form.mode === "always") return null;
  const showFrom = fromToInstant(form.from, timeZone);
  const showUntil = untilToInstant(form.until, timeZone);
  if (showFrom === undefined && showUntil === undefined) return null;
  return {
    ...(showFrom === undefined ? {} : { showFrom }),
    ...(showUntil === undefined ? {} : { showUntil }),
  };
}
